import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  Portlet,
  PortletBody,
  PortletCaption,
  PortletCaptionSubject,
  PortletFooter,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import { OrderOrigin } from '../../constants/OrderOrigin';
import { useStores } from '../../hooks/use-stores';
import TicketDetails from './TicketDetails';
import ReactTable from 'react-table';
import getSortHeaderProps from '../../utils/get-sorted-headers';
import { displayDate } from '../../utils/date';
import { LoaderInline } from '../../components/PageLoading/PageLoading';
import { Radio } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';

const TicketSync = observer((props) => {
  const { ticket, t, onMergeTicket = () => {}, onUnmergeTicket = () => {} } = props;
  const [ loading, setLoading ] = useState(false);
  const [ sorted, setSorted ] = useState([]);
  const [ activeTicket, setActiveTicket ] = useState(null);
  const { ticketStore, newAuthStore, notifierStore } = useStores();
  const { tickets } = ticketStore;
  const { user } = newAuthStore;
  const filteredTickets = tickets.filter(
    v => v.id !== ticket.id
      && v.origin === OrderOrigin.Local
      && ((v.customerId === ticket.customerId) || (v.customerCode === ticket.customerCode))
      && v.mixDesignCode === ticket.mixDesignCode
      && v.mixQuantity === ticket.mixQuantity
  );

  const mergeTicket = () => {
    setLoading(true);

    ticketStore.merge(ticket.id, activeTicket.id)
      .then(() => {
        notifierStore.showInfo('Tickets successfully merged');
        ticketStore.loadTickets();
        onMergeTicket();
      })
      .catch(e => notifierStore.showError(`Couldn't merge tickets ${e.message}`))
      .finally(() => {
        setLoading(false);
        setActiveTicket(null);
      });
  };

  const unmergeTicket = () => {
    setLoading(true);

    ticketStore.unmerge(ticket.id)
      .then(() => {
        notifierStore.showInfo('Ticket successfully unmerge');
        ticketStore.loadTickets();
        onUnmergeTicket();
      })
      .catch(e => notifierStore.showError(`Couldn't unmerge ticket ${e.message}`))
      .finally(() => {
        setLoading(false);
        setActiveTicket(null);
      });
  };

  const getColumns = () => {
    const { timezone } = ticketStore;

    return [
      {
        headerText: '',
        accessor: ticket => ticket,
        Cell: (ticket) => (
          <Radio
            key={ticket.original.id}
            inline
            className="mt-radio mt-radio-outline"
            name={'activeTicket'}
            checked={!!(activeTicket && activeTicket.id === ticket.original.id)}
            onChange={() => {
              setActiveTicket(ticket.original);
            }}
          >
            <span />
            {' '}
          </Radio>
        ),
        width: 30,
        id: 'active',
      },
      {
        headerText: t('ID'),
        accessor: ticket => ticket,
        Cell: ticket => (
          <span title={ticket.original.id}
            className={ticket.original.isVoided ? 'ticket-voided' : ticket.original.isDemo ? 'ticket-demo' : ''}>
            {ticket.original.id}
          </span>
        ),
        id: 'id',
        width: 70,
        className: 'sticky',
        headerClassName: 'sticky',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Date'),
        accessor: ticket => ticket,
        Cell: ticket => (displayDate(ticket.original.loadStartTime, timezone, 'L', user.language)),
        id: 'loadStartTime',
        width: 90,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Time'),
        accessor: ticket => ticket,
        Cell: ticket => (displayDate(ticket.original.loadStartTime, timezone, 'LT', user.language)),
        id: 'loadStartTime',
        width: 70,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Ticket'),
        accessor: ticket => ticket,
        Cell: ticket => (<span>{ticket.original.dispatchTicketCode || ticket.original.id}</span>),
        width: 70,
        id: 'dispatchTicketCode',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Mix'),
        accessor: ticket => ticket,
        Cell: ticket => (<span>{ticket.original.mixDesignCode}</span>),
        id: 'mixDesignCode',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Qty'),
        Cell: ticket => (<span>{(ticket.original.mixQuantity)}</span>),
        id: 'mixQuantity',
        maxWidth: 50,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Truck'),
        accessor: 'truckCode',
        id: 'truckCode',
        maxWidth: 60,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Customer'),
        accessor: 'customerName',
        id: 'customerName',
        ...getSortHeaderProps(sorted),
      },
    ];
  };

  const renderUnmergeButton = () => {
    return (
      <React.Fragment>
        <p>{t('Ticket is merged with the local ticket ')} #{ticket.mergeBatchTicketId}</p>
        <div className="text-right">
          <button className="btn btn-sm btn-primary"
            onClick={() => unmergeTicket()}>
            <i className="fa fa-unlink" /> {t('Unmerge')}
          </button>
        </div>
      </React.Fragment>
    );
  };

  const renderTicketsToMerge = () => {
    return (
      <React.Fragment>
        {!filteredTickets.length
          ? <h4>{t('No local tickets found')}</h4>
          : (
            <div>
              <p>{t('Ticket can be merge with one of the local tickets')}</p>

              {loading
                ? <LoaderInline />
                : (
                  <div>
                    <ReactTable
                      className="-highlight max-height-table"
                      data={filteredTickets}
                      noDataText={' '}
                      columns={getColumns()}
                      sorted={sorted}
                      onSortedChange={sorted => setSorted(sorted)}
                      showPaginationTop={false}
                      showPaginationBottom={false}
                      showPageSizeOptions={false}
                      resizable={false}
                      minRows={1}
                      defaultPageSize={100}
                    />
                    <div className="text-right">
                      <button className="btn btn-sm btn-primary"
                        disabled={!activeTicket}
                        onClick={() => mergeTicket()}>
                        <i className="fa fa-link" /> {t('Merge')}
                      </button>
                    </div>
                  </div>
                )
              }

            </div>
          )
        }
      </React.Fragment>
    );
  };

  return (
    <Portlet>
      <PortletTitle>
        <PortletCaption icon={'fa-link'}>
          <PortletCaptionSubject>{t('Ticket')} {ticket.dispatchTicketCode}</PortletCaptionSubject>
        </PortletCaption>
      </PortletTitle>
      <PortletBody>
        {ticket.mergeBatchTicketId ? renderUnmergeButton() : renderTicketsToMerge()}
      </PortletBody>
      {activeTicket && (
        <PortletFooter>
          <TicketDetails ticket={activeTicket} />
        </PortletFooter>
      )}
    </Portlet>
  );
});

export default withTranslation()(TicketSync);
